import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  StoreIntegration, 
  StoreIntegrationConfiguration,
  DEFAULT_INTEGRATION_CONFIGS 
} from 'app/models/store-integration.models';
import { Integration } from 'app/models/integration.models';

export interface IntegrationConfigDialogData {
  storeId: number;
  integration: Integration;
  existingConfig?: StoreIntegration;
  mode: 'create' | 'edit';
}

@Component({
  selector: 'app-integration-config-dialog',
  templateUrl: './integration-config-dialog.component.html',
  styleUrls: ['./integration-config-dialog.component.css']
})
export class IntegrationConfigDialogComponent implements OnInit {

  configForm: FormGroup;
  saving = false;
  testing = false;

  constructor(
    public dialogRef: MatDialogRef<IntegrationConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IntegrationConfigDialogData,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.configForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadExistingConfiguration();
  }

  /**
   * Crea el formulario reactivo con todos los campos posibles
   */
  private createForm(): FormGroup {
    return this.fb.group({
      // Configuración común
      environment: ['sandbox'],
      
      // Claves y tokens
      publicKey: [''],
      secretKey: [''],
      accessToken: [''],
      clientId: [''],
      clientSecret: [''],
      
      // URLs
      webhookUrl: [''],
      notificationUrl: [''],
      returnUrl: [''],
      cancelUrl: [''],
      
      // Configuración específica
      merchantId: [''],
      apiKey: [''],
      apiSecret: [''],
      
      // Notas
      notes: ['']
    });
  }

  /**
   * Carga la configuración existente si está en modo edición
   */
  private loadExistingConfiguration(): void {
    if (this.data.mode === 'edit' && this.data.existingConfig) {
      const config = this.data.existingConfig.configuration;
      this.configForm.patchValue({
        environment: config.environment || 'sandbox',
        publicKey: config.publicKey || '',
        secretKey: config.secretKey || '',
        accessToken: config.accessToken || '',
        clientId: config.clientId || '',
        clientSecret: config.clientSecret || '',
        webhookUrl: config.webhookUrl || '',
        notificationUrl: config.notificationUrl || '',
        returnUrl: config.returnUrl || '',
        cancelUrl: config.cancelUrl || '',
        merchantId: config.merchantId || '',
        apiKey: config.apiKey || '',
        apiSecret: config.apiSecret || '',
        notes: this.data.existingConfig.notes || ''
      });
    } else {
      // Cargar configuración por defecto
      this.loadDefaultConfiguration();
    }

    // Agregar validaciones según el tipo de integración
    this.addValidationsByIntegrationType();
  }

  /**
   * Carga la configuración por defecto según el tipo de integración
   */
  private loadDefaultConfiguration(): void {
    const integrationName = this.data.integration.name?.toLowerCase() || '';
    const defaultConfig = DEFAULT_INTEGRATION_CONFIGS[integrationName];
    
    if (defaultConfig) {
      this.configForm.patchValue(defaultConfig);
    }
  }

  /**
   * Agrega validaciones específicas según el tipo de integración
   */
  private addValidationsByIntegrationType(): void {
    const integrationType = this.getIntegrationType();
    
    // Limpiar validaciones existentes
    Object.keys(this.configForm.controls).forEach(key => {
      this.configForm.get(key)?.clearValidators();
    });

    switch (integrationType) {
      case 'mercadopago':
        this.configForm.get('publicKey')?.setValidators([Validators.required]);
        this.configForm.get('accessToken')?.setValidators([Validators.required]);
        break;
        
      case 'stripe':
        this.configForm.get('publicKey')?.setValidators([Validators.required]);
        this.configForm.get('secretKey')?.setValidators([Validators.required]);
        break;
        
      case 'paypal':
        this.configForm.get('clientId')?.setValidators([Validators.required]);
        this.configForm.get('clientSecret')?.setValidators([Validators.required]);
        break;
        
      default:
        this.configForm.get('apiKey')?.setValidators([Validators.required]);
        break;
    }

    // Actualizar validaciones
    this.configForm.updateValueAndValidity();
  }

  /**
   * Obtiene el tipo de integración basado en el nombre
   */
  getIntegrationType(): string {
    const name = this.data.integration.name?.toLowerCase() || '';
    
    if (name.includes('mercadopago') || name.includes('mercado pago')) {
      return 'mercadopago';
    } else if (name.includes('stripe')) {
      return 'stripe';
    } else if (name.includes('paypal')) {
      return 'paypal';
    } else if (name.includes('visa')) {
      return 'visa';
    }
    
    return 'default';
  }

  /**
   * Verifica si la configuración básica es válida
   */
  isConfigValid(): boolean {
    const integrationType = this.getIntegrationType();
    const formValue = this.configForm.value;
    
    switch (integrationType) {
      case 'mercadopago':
        return !!(formValue.publicKey && formValue.accessToken);
      case 'stripe':
        return !!(formValue.publicKey && formValue.secretKey);
      case 'paypal':
        return !!(formValue.clientId && formValue.clientSecret);
      default:
        return !!(formValue.apiKey);
    }
  }

  /**
   * Prueba la conexión con la integración (simulado)
   */
  async testConnection(): Promise<void> {
    if (!this.isConfigValid()) {
      this.snackBar.open('Completa los campos obligatorios antes de probar la conexión', 'OK', {
        duration: 3000
      });
      return;
    }

    this.testing = true;
    
    try {
      // Simular test de conexión
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // En una implementación real, aquí harías una llamada al backend para probar la conexión
      const success = Math.random() > 0.3; // 70% de éxito simulado
      
      if (success) {
        this.snackBar.open('✓ Conexión exitosa con la integración', 'OK', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
      } else {
        this.snackBar.open('✗ Error en la conexión. Verifica tus credenciales', 'OK', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    } catch (error) {
      this.snackBar.open('Error al probar la conexión', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    } finally {
      this.testing = false;
    }
  }

  /**
   * Guarda la configuración
   */
  onSave(): void {
    if (!this.configForm.valid) {
      this.markFormGroupTouched();
      return;
    }

    this.saving = true;
    
    const configuration: StoreIntegrationConfiguration = {
      environment: this.configForm.value.environment,
      publicKey: this.configForm.value.publicKey,
      secretKey: this.configForm.value.secretKey,
      accessToken: this.configForm.value.accessToken,
      clientId: this.configForm.value.clientId,
      clientSecret: this.configForm.value.clientSecret,
      webhookUrl: this.configForm.value.webhookUrl,
      notificationUrl: this.configForm.value.notificationUrl,
      returnUrl: this.configForm.value.returnUrl,
      cancelUrl: this.configForm.value.cancelUrl,
      merchantId: this.configForm.value.merchantId,
      apiKey: this.configForm.value.apiKey,
      apiSecret: this.configForm.value.apiSecret
    };

    // Limpiar campos vacíos
    Object.keys(configuration).forEach(key => {
      if (!configuration[key as keyof StoreIntegrationConfiguration]) {
        delete configuration[key as keyof StoreIntegrationConfiguration];
      }
    });

    const result = {
      configuration,
      notes: this.configForm.value.notes
    };

    // Simular guardado
    setTimeout(() => {
      this.saving = false;
      this.dialogRef.close(result);
    }, 1000);
  }

  /**
   * Cancela y cierra el diálogo
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Marca todos los campos como touched para mostrar errores de validación
   */
  private markFormGroupTouched(): void {
    Object.keys(this.configForm.controls).forEach(key => {
      const control = this.configForm.get(key);
      control?.markAsTouched();
    });
  }
}